import React, { useState, useEffect } from 'react';
import { API_URL } from '../lib/utils';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import { Zap, CheckCircle2, Loader2, Send } from 'lucide-react';
import PoweredBy from '../components/ui/PoweredBy';

const PublicJoinPage = () => {
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [form, setForm] = useState({ name: '', company: '', email: '', phone: '', message: '' });
  const [companyName, setCompanyName] = useState("Chargement...");
  const [companyLogo, setCompanyLogo] = useState(null);

  useEffect(() => {
    fetch(`${API_URL}/settings/public-config`)
      .then(res => res.json())
      .then(data => {
        setCompanyName(data.company_name)
        setCompanyLogo(data.logo_base64);
      })
      .catch(() => setCompanyName("Mon Entreprise"));
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/leads/public/join`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form)
      });
      if (res.ok) { setSubmitted(true); toast.success("Demande envoyée !"); }
      else { const err = await res.json(); toast.error(err.detail || "Erreur lors de l'envoi"); }
    } catch (error) { toast.error("Le serveur est injoignable"); }
    finally { setLoading(false); }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center p-4">
        <Card className="max-w-md w-full text-center p-8 shadow-2xl bg-white border-none text-slate-900">
          <CheckCircle2 className="w-20 h-20 text-emerald-500 mx-auto mb-6 animate-in zoom-in duration-500" />
          <h2 className="text-3xl font-bold mb-3">Demande reçue !</h2>
          <p className="text-slate-600 mb-8 text-lg">Merci <strong>{form.name}</strong>, notre équipe vous recontacte très prochainement.</p>
          <Button onClick={() => window.location.reload()} className="w-full h-12 text-lg">Terminer</Button>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-100 via-slate-50 to-slate-200 flex flex-col items-center justify-center py-12 px-4 font-sans text-slate-900">
      
      {/* BRANDING HEADER CENTRÉ */}
      <div className="max-w-2xl w-full text-center mb-10 animate-in fade-in slide-in-from-top-4 duration-700">
        <div className="flex justify-center items-center gap-4 mb-4">
          {companyLogo ? (
            <img src={companyLogo} alt="Logo Entreprise" className="h-16 w-auto object-contain drop-shadow-sm" />
          ) : (
            <div className="w-14 h-14 bg-primary rounded-2xl flex items-center justify-center shadow-lg">
              <Zap className="w-7 h-7 text-white fill-white" />
            </div>
          )}
          <h1 className="text-4xl font-extrabold tracking-tight text-slate-900">{companyName}</h1>
        </div>
      </div>

      <Card className="max-w-xl w-full shadow-xl border-slate-200/60 bg-white rounded-2xl overflow-hidden animate-in zoom-in-95 duration-500">
        <div className="bg-slate-50 border-b border-slate-100 p-8 text-center">
            <h2 className="text-2xl font-bold text-slate-900">Devenez partenaire</h2>
            <p className="text-slate-500 mt-2">Parlez-nous de votre projet, nous vous répondons rapidement.</p>
        </div>
        <CardContent className="p-8">
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-2">
                  <Label className="text-slate-700 font-medium">Nom Complet *</Label>
                  <Input required className="bg-slate-50 focus:bg-white text-slate-900" placeholder="Jean Dupont" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} />
              </div>
              <div className="space-y-2">
                  <Label className="text-slate-700 font-medium">Entreprise</Label>
                  <Input className="bg-slate-50 focus:bg-white text-slate-900" placeholder="Société SAS" value={form.company} onChange={e => setForm({ ...form, company: e.target.value })} />
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-2">
                  <Label className="text-slate-700 font-medium">Email Professionnel *</Label>
                  <Input type="email" required className="bg-slate-50 focus:bg-white text-slate-900" placeholder="jean@societe.com" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} />
              </div>
              <div className="space-y-2">
                  <Label className="text-slate-700 font-medium">Téléphone</Label>
                  <Input type="tel" className="bg-slate-50 focus:bg-white text-slate-900" placeholder="06 12 34 56 78" value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} />
              </div>
            </div>
            <div className="space-y-2">
                <Label className="text-slate-700 font-medium">Votre besoin *</Label>
                <Textarea required rows={5} className="bg-slate-50 focus:bg-white text-slate-900 resize-none" placeholder="Décrivez votre projet ici..." value={form.message} onChange={e => setForm({ ...form, message: e.target.value })} />
            </div>
            
            <div className="pt-4">
                <Button type="submit" className="w-full h-14 text-lg font-bold shadow-lg hover:shadow-xl transition-all hover:-translate-y-0.5" disabled={loading}>
                {loading ? <Loader2 className="animate-spin mr-2" /> : <Send className="w-5 h-5 mr-2" />}
                Envoyer ma demande
                </Button>
            </div>
          </form>
        </CardContent>
      </Card>

      <footer className="mt-12 text-slate-500 flex flex-col items-center">
        <PoweredBy />
      </footer>
    </div>
  );
};

export default PublicJoinPage;